import { useMemo } from "react";

import type { UnactivatedMember } from "@storyboard/web-client";
import {
  computeDriverRoster,
  computeDriverTotalCount,
  computeDriverVolatile,
  driverRosterCollections,
  driverTotalCountCollections,
  driverVolatileCollections,
  rosterNeedsAvailability,
} from "@/models/repositories/Driver";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { DriverFilterConfig, DriverRow, TagDefinition } from "@/types/driver";
import { dedupeDriversByMembership } from "./dedupeDriversByMembership";
import { applyLocationFilter, applySortFromVolatile } from "./driverSort";
import { isInitialDriverLoadPending } from "./initialDriverLoadPending";
import { applyColumnSort, applyTagFilters, type ColumnSort } from "./tagFilters";
import { membershipIdsWithFreshHeartbeat } from "./unactivatedHeartbeats";
import { unactivatedMemberMatchesSearch } from "./unactivatedMemberSearch";

// Shared fallback for queries whose first run hasn't resolved. A literal `?? []` mints a new array
// identity every render, which breaks every downstream memo keyed on it; the churned `drivers`
// identity then re-triggers TanStack Table's auto page-index reset (a queued setState) on each
// render — a self-sustaining render loop for as long as any battery query is still pending.
const NO_ROWS: never[] = [];

function displayNameFromUnactivatedMember(m: UnactivatedMember): string | null {
  return m.account?.name ?? m.invite?.name ?? m.invite?.email ?? m.invite?.phoneNumber ?? null;
}

function syntheticDriverRowForUnactivated(
  m: UnactivatedMember,
  workspaceId: string,
  tagValueMap: Map<string, Record<string, string[]>>,
): DriverRow {
  return {
    accountId: m.account?.id ?? null,
    workspaceMembershipId: m.membershipId,
    feedId: null,
    workspaceId,
    name: displayNameFromUnactivatedMember(m),
    alias: null,
    recentMessage: null,
    group: null,
    groupId: null,
    location: null,
    country: null,
    stateProvince: null,
    volume: null,
    feedPrefId: null,
    unread: null,
    calculatedAvailability: null,
    offline: null,
    mostRecentDrivingStatus: null,
    mostRecentDrivingStatusChangedAt: null,
    mostRecentDutyStatusStatus: null,
    mostRecentDutyStatusChangedAt: null,
    mostRecentHandsFreeStatusEnabled: null,
    mostRecentHandsFreeStatusUpdatedAt: null,
    mostRecentVolumeAudible: null,
    membershipAvailabilityUpdatedAt: null,
    latitude: null,
    longitude: null,
    locationUpdatedAt: null,
    locationSource: null,
    locationPlaceName: null,
    tags: tagValueMap.get(m.membershipId) ?? {},
    hasPersonalDevice: false,
  };
}

export function useDriverQuery(
  workspaceId: string,
  membershipId: string | undefined,
  config: DriverFilterConfig,
  tagValueMap: Map<string, Record<string, string[]>>,
  tagFilters: Record<string, string[]>,
  columnSort: ColumnSort | null,
  unactivatedMembers: UnactivatedMember[] = [],
  tags: readonly TagDefinition[] = [],
) {
  // The roster subscribes to `wsMembershipAvailability` only when an availability-based filter is
  // active, so a driver heartbeat otherwise re-runs only the lighter volatile compute, never the
  // whole roster scan. The compute skips the read under the same condition.
  const rosterCollections = rosterNeedsAvailability(config)
    ? driverRosterCollections
    : driverRosterCollections.filter((name) => name !== "wsMembershipAvailability");
  const rosterRows = useCollectionsCompute(
    rosterCollections,
    () =>
      workspaceId && membershipId
        ? computeDriverRoster({
            workspaceId,
            membershipId,
            myCurrentWorkspaceMembershipId: membershipId,
            filter: config,
          })
        : null,
    [workspaceId, membershipId, config],
    "DriverView:driverRoster",
  );
  const rosterResult = rosterRows ?? NO_ROWS;

  const volatileRows = useCollectionsCompute(
    driverVolatileCollections,
    () =>
      workspaceId && membershipId
        ? computeDriverVolatile({ workspaceId, membershipId, language: config.language })
        : null,
    [workspaceId, membershipId, config.language],
    "DriverView:driverVolatile",
  );
  const volatileResult = volatileRows ?? NO_ROWS;

  const teamAliasesKey = config.teamAliases.join(",");
  const totalCount = useCollectionsCompute(
    driverTotalCountCollections,
    () =>
      workspaceId && membershipId
        ? computeDriverTotalCount({ workspaceId, membershipId, teamAliases: config.teamAliases })
        : null,
    [workspaceId, membershipId, teamAliasesKey],
    "DriverView:driverTotalCount",
  );

  // The roster/volatile/count trio is the heavy first-render battery: while any of them is still
  // null (executing its first run), the driver view must keep interaction blocked (WEB-2144).
  const initialLoadPending = isInitialDriverLoadPending({
    workspaceId,
    membershipId,
    results: [rosterRows, volatileRows, totalCount],
  });

  // "My Drivers" is a client-side view over the shared roster rows rather than its own SQL variant:
  // the roster and count queries are tab-independent, so switching tabs re-filters cached rows
  // instead of recomputing the whole roster while the
  // first one is still running (WEB-2136). The never-logged-in filter targets unactivated members,
  // who can't have feed preferences, so it keeps the full row set (matching the previous SQL).
  const onlyNeverLoggedIn = config.statusFilter.length === 1 && config.statusFilter[0] === "never-logged-in";
  const tabRoster = useMemo(() => {
    if (config.tab !== "my" || onlyNeverLoggedIn) return rosterResult;
    return rosterResult.filter((r) => r.feedPrefId != null);
  }, [rosterResult, config.tab, onlyNeverLoggedIn]);

  const unactivatedLabelByMembershipId = useMemo(() => {
    const m = new Map<string, string>();
    for (const member of unactivatedMembers) {
      const label = displayNameFromUnactivatedMember(member);
      if (label) m.set(member.membershipId, label);
    }
    return m;
  }, [unactivatedMembers]);

  // Unactivated members that are freshly heartbeating — i.e. they just logged in and the REST-fetched set
  // hasn't caught up. Derived from the volatile rows rather than the filtered `drivers` array so a
  // just-activated driver hidden by the active filters still signals DriverView to refetch the set.
  const freshlyHeartbeatingUnactivatedIds = useMemo(() => {
    const unactivatedIds = new Set(unactivatedMembers.map((m) => m.membershipId));
    return membershipIdsWithFreshHeartbeat(volatileResult, unactivatedIds);
  }, [volatileResult, unactivatedMembers]);

  const visibleMembershipIds = useMemo(() => {
    const ids = new Set<string>(tabRoster.map((r) => r.workspaceMembershipId));
    if (
      config.statusFilter.length === 1 &&
      config.statusFilter[0] === "never-logged-in" &&
      unactivatedMembers.length > 0 &&
      config.groupIds.length === 0
    ) {
      for (const m of unactivatedMembers) {
        if (ids.has(m.membershipId)) continue;
        if (
          !unactivatedMemberMatchesSearch(m, config.search, config.searchTargets, tagValueMap.get(m.membershipId), tags)
        )
          continue;
        ids.add(m.membershipId);
      }
    }
    return ids;
  }, [
    tabRoster,
    config.statusFilter,
    config.search,
    config.searchTargets,
    config.groupIds,
    unactivatedMembers,
    tagValueMap,
    tags,
  ]);

  const { drivers, allAliases } = useMemo(() => {
    const perFeedVolatile = new Map(volatileResult.map((v) => [v.feedId, v]));

    const rosterFeedIds = new Set(tabRoster.map((r) => r.feedId).filter((id): id is string => id != null));
    const teamVolatile = volatileResult.filter((v) => rosterFeedIds.has(v.feedId));
    const driverAgg = config.tab === "all" ? aggregateByDriver(teamVolatile) : null;

    const sortMap = new Map<string, { unread: boolean | null; latestItemCreatedAt: string | null }>();
    const personalDeviceIds = new Set(config.personalDeviceMembershipIds);

    const merged: DriverRow[] = tabRoster.map((r) => {
      const sortKey = r.feedId ?? r.workspaceMembershipId;
      const feedVol = r.feedId != null ? perFeedVolatile.get(r.feedId) : undefined;
      const agg = driverAgg?.get(r.workspaceMembershipId);

      const unread = driverAgg ? (agg?.unread ?? null) : (feedVol?.unread ?? null);
      const latestItemCreatedAt = driverAgg
        ? (agg?.latestItemCreatedAt ?? null)
        : (feedVol?.latestItemCreatedAt ?? null);
      const recentMessage = driverAgg ? (agg?.recentMessage ?? null) : (feedVol?.recentMessage ?? null);

      sortMap.set(sortKey, { unread, latestItemCreatedAt });

      const apiLabel = unactivatedLabelByMembershipId.get(r.workspaceMembershipId);
      const name = r.name ?? apiLabel ?? null;

      return {
        ...r,
        name,
        recentMessage,
        unread,
        // Availability/location/status columns are sourced from the volatile query (see Driver.ts):
        // same per-membership values, but kept live without re-running the heavy roster on heartbeats.
        location: feedVol?.location ?? null,
        volume: feedVol?.volume ?? null,
        country: feedVol?.country ?? null,
        stateProvince: feedVol?.stateProvince ?? null,
        calculatedAvailability: feedVol?.calculatedAvailability ?? null,
        offline: feedVol?.offline ?? null,
        mostRecentHandsFreeStatusEnabled: feedVol?.mostRecentHandsFreeStatusEnabled ?? null,
        mostRecentHandsFreeStatusUpdatedAt: feedVol?.mostRecentHandsFreeStatusUpdatedAt ?? null,
        mostRecentVolumeAudible: feedVol?.mostRecentVolumeAudible ?? null,
        mostRecentDrivingStatus: feedVol?.mostRecentDrivingStatus ?? null,
        mostRecentDutyStatusStatus: feedVol?.mostRecentDutyStatusStatus ?? null,
        mostRecentDutyStatusChangedAt: feedVol?.mostRecentDutyStatusChangedAt ?? null,
        mostRecentDrivingStatusChangedAt: feedVol?.mostRecentDrivingStatusChangedAt ?? null,
        membershipAvailabilityUpdatedAt: feedVol?.membershipAvailabilityUpdatedAt ?? null,
        latitude: feedVol?.latitude ?? null,
        longitude: feedVol?.longitude ?? null,
        locationUpdatedAt: feedVol?.locationUpdatedAt ?? null,
        locationSource: feedVol?.locationSource ?? null,
        locationPlaceName: feedVol?.locationPlaceName ?? null,
        tags: tagValueMap.get(r.workspaceMembershipId) ?? {},
        hasPersonalDevice: personalDeviceIds.has(r.workspaceMembershipId),
      };
    });

    const dedupedMerged = dedupeDriversByMembership(merged);

    if (
      config.statusFilter.length === 1 &&
      config.statusFilter[0] === "never-logged-in" &&
      unactivatedMembers.length > 0
    ) {
      const have = new Set(dedupedMerged.map((d) => d.workspaceMembershipId));
      const skipSyntheticForGroupFilter = config.groupIds.length > 0;
      for (const m of unactivatedMembers) {
        if (have.has(m.membershipId)) continue;
        if (skipSyntheticForGroupFilter) continue;
        if (
          !unactivatedMemberMatchesSearch(m, config.search, config.searchTargets, tagValueMap.get(m.membershipId), tags)
        )
          continue;
        dedupedMerged.push(syntheticDriverRowForUnactivated(m, workspaceId, tagValueMap));
      }
    }

    const sorted = applySortFromVolatile(dedupedMerged, sortMap, config.sort);
    const locationFiltered = config.location ? applyLocationFilter(sorted, config.location) : sorted;

    const tagFiltered = applyTagFilters(locationFiltered, tagFilters);
    const columnSorted = columnSort ? applyColumnSort(tagFiltered, columnSort) : tagFiltered;

    const aliases = [
      ...new Set(
        columnSorted
          .map((d) => d.alias)
          .filter((a): a is string => a != null && a.length > 0)
          .map((a) => a.toLowerCase()),
      ),
    ].sort();

    const filtered =
      config.aliasNames.length === 0
        ? columnSorted
        : columnSorted.filter((d) => d.alias != null && config.aliasNames.includes(d.alias.toLowerCase()));

    return { drivers: filtered, allAliases: aliases };
  }, [
    tabRoster,
    volatileResult,
    config.sort,
    config.tab,
    config.location,
    config.aliasNames,
    config.statusFilter,
    config.search,
    config.searchTargets,
    config.groupIds,
    config.personalDeviceMembershipIds,
    workspaceId,
    tagValueMap,
    tagFilters,
    columnSort,
    unactivatedLabelByMembershipId,
    unactivatedMembers,
    tags,
  ]);

  const totalDriverCount = totalCount ? (config.tab === "my" ? totalCount.myCount : totalCount.allCount) : 0;

  return {
    drivers,
    allAliases,
    totalDriverCount,
    visibleMembershipIds,
    initialLoadPending,
    freshlyHeartbeatingUnactivatedIds,
  };
}

interface VolatileRow {
  aliasMembershipId: string | null;
  unread: boolean | null;
  latestItemCreatedAt: string | null;
  recentMessage: string | null;
}

interface DriverAggregate {
  unread: boolean;
  latestItemCreatedAt: string | null;
  recentMessage: string | null;
}

function aggregateByDriver(rows: VolatileRow[]): Map<string, DriverAggregate> {
  const map = new Map<string, DriverAggregate>();

  for (const v of rows) {
    if (!v.aliasMembershipId) continue;
    const existing = map.get(v.aliasMembershipId);

    if (!existing) {
      map.set(v.aliasMembershipId, {
        unread: v.unread ?? false,
        latestItemCreatedAt: v.latestItemCreatedAt,
        recentMessage: v.recentMessage,
      });
      continue;
    }

    existing.unread = existing.unread || (v.unread ?? false);
    if (
      v.latestItemCreatedAt &&
      (!existing.latestItemCreatedAt || v.latestItemCreatedAt > existing.latestItemCreatedAt)
    ) {
      existing.latestItemCreatedAt = v.latestItemCreatedAt;
      existing.recentMessage = v.recentMessage;
    }
  }

  return map;
}
